import React, { useEffect } from 'react';
import { CelebrationDrawerCta, CelebrationDrawerFrame } from '@components/ui/celebration-drawer';
import { DrawerDescription, DrawerTitle } from '@components/ui/drawer';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { GoingCelebrationIllustration } from './GoingCelebrationIllustration';

/**
 * Post-"Going" celebration (design §7). Single merged heading (QA #8).
 * Replaces the generic first-RSVP EventGoingWelcome onboarding drawer for users
 * who reach this via a claimed `event_going` intent (CelebrationHost marks that
 * flow complete on mount).
 */
export const EventGoingCelebrationDrawer: React.FC<{
  onClose: () => void;
  /** Attribution from the stored intent/CTA (null when unknown) — reported to
   * analytics only, never gates whether this drawer shows (onboarding-design.md §7). */
  source?: string | null;
}> = ({ onClose, source }) => {
  const { trackEvent } = useAmplitudeTracking();
  useEffect(() => {
    // Conditional spread, not `source: source ?? null` — useAmplitudeTracking merges
    // {...getBasePayload(), ...properties}, and getBasePayload already sets `source` from
    // session attribution. An unconditional key overwrites real attribution with null, and
    // null is the common case: EventGuestPage and LiveSpaceSlot both stash GOING_EVENT
    // intents with no source at all.
    trackEvent(ANALYTICS_EVENTS.event_going_welcome_viewed, {
      ...(source ? { source } : {}),
    });
  }, [trackEvent, source]);

  return (
    <CelebrationDrawerFrame
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <div className="flex flex-col items-center gap-2.5 pt-2 text-center">
        {/* No exclamation mark, as the Going toasts, the ticketed drawer and the PIN sheet say the
            same moment; a full stop keeps the two parts of the merged heading apart when read.
            "People going", as the PIN card says it: the tone of voice speaks of people, not
            attendees. */}
        <DrawerTitle className="font-livvic text-2xl font-bold text-black">
          You&apos;re going. <span className="text-mb-teal">Connect with people going</span>
        </DrawerTitle>
        <DrawerDescription className="font-fira text-sm text-mb-dark-gray">
          State what you need and find people that can help you become even more extraordinary. Or
          find people who might need your help.
        </DrawerDescription>
      </div>
      <div className="w-full px-2 pt-3">
        <GoingCelebrationIllustration />
      </div>
      <CelebrationDrawerCta onClick={onClose}>Continue</CelebrationDrawerCta>
    </CelebrationDrawerFrame>
  );
};
